import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { useCRMAuth } from '@/contexts/CRMAuthContext';
import CRMLayout from './CRMLayout';
import {
  TrendingUp, TrendingDown, DollarSign, Users, Briefcase,
  Target, PieChart, BarChart3, Loader2, ArrowUpRight, ArrowDownRight
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

export default function CRMKPIDashboard() {
  const { token } = useCRMAuth();
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('month');
  const [kpiData, setKpiData] = useState(null);

  useEffect(() => {
    fetchKPIData();
  }, [period]);

  const fetchKPIData = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/crm/kpi/dashboard?period=${period}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        setKpiData(await res.json());
      }
    } catch (err) {
      console.error('Failed to fetch KPI data:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (val) => `₹${(val || 0).toLocaleString()}`;
  const formatPercent = (val) => `${(val || 0).toFixed(1)}%`;

  if (loading) {
    return (
      <CRMLayout>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
        </div>
      </CRMLayout>
    );
  }

  const data = kpiData || {
    revenue: { current: 0, previous: 0, growth: 0 },
    profit: { current: 0, margin: 0 },
    clients: { active: 0, new: 0, churned: 0 },
    production: { total_items: 0, total_value: 0, avg_per_day: 0 },
    team: { total: 0, active: 0, productivity: 0 },
    top_clients: [],
    top_performers: []
  };

  return (
    <CRMLayout>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">KPI Dashboard</h1>
          <p className="text-slate-500">Financial & operational metrics</p>
        </div>
        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger className="w-[150px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="week">This Week</SelectItem>
            <SelectItem value="month">This Month</SelectItem>
            <SelectItem value="quarter">This Quarter</SelectItem>
            <SelectItem value="year">This Year</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Main KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {/* Revenue */}
        <Card className="p-5 bg-gradient-to-br from-emerald-500 to-emerald-600 text-white">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm opacity-90">Total Revenue</p>
              <p className="text-3xl font-bold mt-1">{formatCurrency(data.revenue?.current)}</p>
              <div className="flex items-center gap-1 mt-2">
                {data.revenue?.growth >= 0 ? (
                  <ArrowUpRight className="w-4 h-4" />
                ) : (
                  <ArrowDownRight className="w-4 h-4" />
                )}
                <span className="text-sm">{formatPercent(Math.abs(data.revenue?.growth))} vs last period</span>
              </div>
            </div>
            <DollarSign className="w-10 h-10 opacity-30" />
          </div>
        </Card>

        {/* Profit */}
        <Card className="p-5 bg-gradient-to-br from-blue-500 to-blue-600 text-white">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm opacity-90">Net Profit</p>
              <p className="text-3xl font-bold mt-1">{formatCurrency(data.profit?.current)}</p>
              <div className="flex items-center gap-1 mt-2">
                <span className="text-sm">Margin: {formatPercent(data.profit?.margin)}</span>
              </div>
            </div>
            <TrendingUp className="w-10 h-10 opacity-30" />
          </div>
        </Card>

        {/* Active Clients */}
        <Card className="p-5 bg-gradient-to-br from-purple-500 to-purple-600 text-white">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm opacity-90">Active Clients</p>
              <p className="text-3xl font-bold mt-1">{data.clients?.active || 0}</p>
              <div className="flex items-center gap-2 mt-2 text-sm">
                <span className="text-emerald-200">+{data.clients?.new || 0} new</span>
                {data.clients?.churned > 0 && <span className="text-red-200">-{data.clients?.churned} churned</span>}
              </div>
            </div>
            <Briefcase className="w-10 h-10 opacity-30" />
          </div>
        </Card>

        {/* Production Value */}
        <Card className="p-5 bg-gradient-to-br from-amber-500 to-amber-600 text-white">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm opacity-90">Production Value</p>
              <p className="text-3xl font-bold mt-1">{formatCurrency(data.production?.total_value)}</p>
              <div className="flex items-center gap-1 mt-2">
                <span className="text-sm">{data.production?.total_items || 0} items delivered</span>
              </div>
            </div>
            <Target className="w-10 h-10 opacity-30" />
          </div>
        </Card>
      </div>

      {/* Secondary Stats */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        {/* Team Performance */}
        <Card className="p-5">
          <div className="flex items-center gap-2 mb-4">
            <Users className="w-5 h-5 text-indigo-600" />
            <h3 className="font-semibold text-slate-900">Team Overview</h3>
          </div>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-slate-600">Total Members</span>
              <span className="font-semibold">{data.team?.total || 0}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-slate-600">Active Today</span>
              <span className="font-semibold text-emerald-600">{data.team?.active || 0}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-slate-600">Avg Productivity</span>
              <span className="font-semibold">{formatPercent(data.team?.productivity)}</span>
            </div>
          </div>
        </Card>

        {/* Top Clients by Revenue */}
        <Card className="p-5">
          <div className="flex items-center gap-2 mb-4">
            <PieChart className="w-5 h-5 text-indigo-600" />
            <h3 className="font-semibold text-slate-900">Top Clients</h3>
          </div>
          {(!data.top_clients || data.top_clients.length === 0) ? (
            <p className="text-sm text-slate-500 text-center py-4">No data yet</p>
          ) : (
            <div className="space-y-3">
              {data.top_clients.slice(0, 5).map((client, idx) => (
                <div key={idx} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 bg-indigo-100 rounded-full flex items-center justify-center text-xs font-medium text-indigo-600">
                      {idx + 1}
                    </div>
                    <span className="text-sm text-slate-700 truncate max-w-[120px]">{client.name}</span>
                  </div>
                  <span className="text-sm font-medium text-emerald-600">{formatCurrency(client.value)}</span>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Top Performers */}
        <Card className="p-5">
          <div className="flex items-center gap-2 mb-4">
            <BarChart3 className="w-5 h-5 text-indigo-600" />
            <h3 className="font-semibold text-slate-900">Top Performers</h3>
          </div>
          {(!data.top_performers || data.top_performers.length === 0) ? (
            <p className="text-sm text-slate-500 text-center py-4">No data yet</p>
          ) : (
            <div className="space-y-3">
              {data.top_performers.slice(0, 5).map((emp, idx) => (
                <div key={idx} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 bg-amber-100 rounded-full flex items-center justify-center text-xs font-medium text-amber-600">
                      {idx + 1}
                    </div>
                    <span className="text-sm text-slate-700 truncate max-w-[120px]">{emp.name}</span>
                  </div>
                  <Badge variant="outline">{emp.items} items</Badge>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      {/* Quick Insights */}
      <Card className="p-5">
        <h3 className="font-semibold text-slate-900 mb-4">Quick Insights</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="p-4 bg-slate-50 rounded-lg">
            <p className="text-sm text-slate-500">Avg Revenue per Client</p>
            <p className="text-xl font-semibold text-slate-900">
              {formatCurrency(data.clients?.active > 0 ? data.revenue?.current / data.clients?.active : 0)}
            </p>
          </div>
          <div className="p-4 bg-slate-50 rounded-lg">
            <p className="text-sm text-slate-500">Avg Production per Day</p>
            <p className="text-xl font-semibold text-slate-900">{data.production?.avg_per_day || 0} items</p>
          </div>
          <div className="p-4 bg-slate-50 rounded-lg">
            <p className="text-sm text-slate-500">Value per Team Member</p>
            <p className="text-xl font-semibold text-slate-900">
              {formatCurrency(data.team?.total > 0 ? data.production?.total_value / data.team?.total : 0)}
            </p>
          </div>
        </div>
      </Card>
    </CRMLayout>
  );
}
